import { z } from "zod";
import { splitRuleSchema } from "./expense-split.js";
import { expenseSplitPayloadSchema, refineExpenseSplitInvariants } from "./expenses.js";

/**
 * Shared DTO — Admin oversight split/participant/dependent + can thiệp sửa split
 * (US-E14.F6-0175 · @UC9202 @UC9203 @UC9204). Một nguồn sự thật cho api (S02 read + S03 mutation) và
 * admin web epic riêng. Mirror style `admin-expenses.ts`. @gate money · rbac · ledger 🔴.
 *
 * BẢO MẬT (invariants — LOAD-BEARING):
 * - REDACTION theo vai: `shareMinor` / `amountMinor` / `fundPortionMinor` = STRING BigInt cho
 *   SUPER_ADMIN/OPS, `null` cho SUPPORT/READ_ONLY (reuse MONEY_VISIBLE_ROLES ở service). Contract cho
 *   phép `string | null` để biểu diễn cả 2 vai — invariant redaction thực thi Ở SERVICE, KHÔNG ở client.
 * - B1: tiền = STRING minor-units (regex số nguyên, no float/number JS).
 * - C3: `.strict()` — KHÔNG khai báo secret / PII thô. `displayName` = nickname per-group (đã redact),
 *   KHÔNG lộ userId / email / fullName.
 * - Mutation `reason` BẮT BUỘC (audit trail can thiệp — bài học 0160 C1 takedown note). Client KHÔNG gửi
 *   `shareMinor` (server tính qua engine F3) — reuse `expenseSplitPayloadSchema` (participant input
 *   cho phép shareMinor CHỈ rule custom, service validate).
 */

/** Số nguyên minor-units dạng string (BigInt-safe, no-float). Nullable = redacted cho vai thấp. */
const MONEY_STRING = /^\d+$/;
const moneyOrNull = z.union([z.string().regex(MONEY_STRING, "tiền phải là số nguyên minor units"), z.null()]);

/**
 * 1 participant trong response oversight. XOR chủ thể (mirror DB): đúng MỘT groupMemberId | dependentId.
 * `displayName` = nickname member (GroupMember.displayName) HOẶC Dependent.displayName (redact-safe).
 * `shareMinor` STRING | null (redaction theo vai). `weight` number|null (input tính, KHÔNG tiền).
 */
export const adminExpenseSplitParticipantSchema = z
  .object({
    participantId: z.string().uuid(),
    groupMemberId: z.string().uuid().nullable(),
    dependentId: z.string().uuid().nullable(),
    displayName: z.string().nullable(),
    shareMinor: moneyOrNull,
    weight: z.number().nullable(),
    isResidualHolder: z.boolean(),
  })
  .strict();
export type AdminExpenseSplitParticipant = z.infer<typeof adminExpenseSplitParticipantSchema>;

/**
 * Response GET /v1/admin/expenses/:expenseId/splits (S02 · @UC9202) — chi tiết chia suất 1 khoản chi:
 * ai gánh bao nhiêu (participant + shareMinor), dependent, phần quỹ (fundPortion). Redaction theo vai.
 */
export const adminExpenseSplitResponseSchema = z
  .object({
    expenseId: z.string().uuid(),
    fundId: z.string().uuid(),
    status: z.string(),
    splitRule: splitRuleSchema,
    /** Tổng số tiền khoản chi (STRING BigInt) — null nếu vai không xem tiền. */
    amountMinor: moneyOrNull,
    /** Phần quỹ gánh (mixed/fund-flow) — null nếu vai không xem tiền. */
    fundPortionMinor: moneyOrNull,
    currency: z.string(),
    participants: z.array(adminExpenseSplitParticipantSchema),
  })
  .strict();
export type AdminExpenseSplitResponse = z.infer<typeof adminExpenseSplitResponseSchema>;

/**
 * Payload POST /v1/admin/expenses/:expenseId/splits/intervene (S03 · @UC9203 @UC9204) — can thiệp sửa
 * split = REVERSAL + repost (append-only). Reuse `expenseSplitPayloadSchema` (rule + source +
 * participants + fundPortionMinor + optInPayOnBehalf) NHƯNG đổi tên rule field → `newSplitRule` cho rõ
 * ý nghĩa "chia lại". `reason` BẮT BUỘC (audit). `.strict` reject field lạ. Client KHÔNG gửi shareMinor
 * (trừ rule custom — service validate).
 */
export const adminInterveneSplitSchema = expenseSplitPayloadSchema
  .innerType() // .omit/.extend cần ZodObject ⇒ gỡ vỏ effects...
  .omit({ splitRule: true })
  .extend({
    /** Quy tắc chia mới (đổi tên `splitRule` → nhấn mạnh "chia lại"). */
    newSplitRule: splitRuleSchema,
    /** Lý do can thiệp — BẮT BUỘC (audit trail; ghi vào audit_log diff). */
    reason: z.string().trim().min(1, "reason bắt buộc (audit can thiệp)").max(500),
  })
  .strict()
  // 🔴 US-AIF.P2-0253b (security review MEDIUM): ...rồi ĐẮP LẠI bất biến từ NGUỒN CHUNG. `.innerType()`
  // ở trên rụng superRefine ⇒ biên intervene TỪNG nhận payload sai luật (payerMemberId trên quỹ-trả-100%,
  // participants rỗng, fundPortionMinor trên allocated) và chỉ "an toàn" nhờ service tình cờ DROP field —
  // brittle. Nay intervene fail-fast 400 tại biên, CÙNG luật với create, không drift.
  .superRefine((val, ctx) => refineExpenseSplitInvariants(val, ctx));
export type AdminInterveneSplitPayload = z.infer<typeof adminInterveneSplitSchema>;

/** Response mutation intervene — trạng thái + tx REVERSAL/repost (append-only chứng minh). */
export const adminInterveneSplitResponseSchema = z
  .object({
    status: z.literal("reposted"),
    reversalTxId: z.string().uuid().nullable(),
    ledgerTxId: z.string().uuid().nullable(),
  })
  .strict();
export type AdminInterveneSplitResponse = z.infer<typeof adminInterveneSplitResponseSchema>;
